"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/rac/carousel";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useTestimonialsRefresh } from "@/hooks/use-testimonials-refresh";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { m } from "framer-motion";
import { RefreshCw } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { TestimonialCard } from "./card";
import { Testimonial, transformReviewToTestimonial } from "./index";

// Extend dayjs with relativeTime plugin
dayjs.extend(relativeTime);
interface TestimonialsClientProps {
  testimonials: Testimonial[];
}
export const TestimonialsClient: React.FC<TestimonialsClientProps> = t0 => {
  const $ = _c(36);
  const {
    testimonials
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      triggerOnce: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    ref: titleRef
  } = useScrollAnimation(t1);
  const [api, setApi] = React.useState();
  const [currentTestimonials, setCurrentTestimonials] = useState(testimonials);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      limit: 10
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    testimonials: refreshedReviews,
    isLoading,
    refreshTestimonials
  } = useTestimonialsRefresh(t2);
  let t3;
  if ($[2] !== refreshedReviews) {
    t3 = refreshedReviews.map(transformReviewToTestimonial);
    $[2] = refreshedReviews;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const refreshedTestimonials = t3;
  let t4;
  if ($[4] !== refreshedTestimonials) {
    t4 = () => {
      if (refreshedTestimonials.length > 0) {
        setCurrentTestimonials(refreshedTestimonials);
      }
    };
    $[4] = refreshedTestimonials;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== api || $[7] !== refreshedTestimonials) {
    t5 = [refreshedTestimonials, api];
    $[6] = api;
    $[7] = refreshedTestimonials;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== refreshTestimonials) {
    t6 = async () => {
      await refreshTestimonials();
    };
    $[9] = refreshTestimonials;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleRefresh = t6;
  let t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <m.h2 id="testimonials-heading" className="text-3xl md:text-4xl font-bold mb-4" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: "easeOut"
    }}>What Our Customers Say</m.h2>;
    t8 = <p className="text-muted-foreground max-w-2xl mx-auto">Read from our satisfied customers about their experience with our services.</p>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  let t9;
  if ($[13] !== titleRef) {
    t9 = <div ref={titleRef} className="max-w-4xl mx-auto text-center mb-12">{t7}{t8}</div>;
    $[13] = titleRef;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = `w-4 h-4 ${isLoading ? "animate-spin" : ""}`;
  let t11;
  if ($[15] !== t10) {
    t11 = <RefreshCw className={t10} />;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-sm font-medium">More Reviews</span>;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== handleRefresh || $[19] !== isLoading || $[20] !== t11) {
    t13 = <div className="flex justify-end mb-1"><Button onClick={handleRefresh} isDisabled={isLoading} className="flex items-center gap-2" aria-label="Load different reviews" variant="outline">{t11}{t12}</Button></div>;
    $[18] = handleRefresh;
    $[19] = isLoading;
    $[20] = t11;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      align: "center",
      loop: true,
      slidesToScroll: 1
    };
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== api || $[24] !== currentTestimonials) {
    let t16;
    if ($[26] !== api) {
      t16 = (testimonial, index) => <CarouselItem key={testimonial.id} className="pl-2 md:pl-4 basis-[80%] sm:basis-[70%] md:basis-[60%]"><div className="h-full px-1" onClick={() => api?.scrollTo(index)}><TestimonialCard testimonial={testimonial} className="transition-all duration-300 hover:shadow-md cursor-pointer" /></div></CarouselItem>;
      $[26] = api;
      $[27] = t16;
    } else {
      t16 = $[27];
    }
    t15 = currentTestimonials.map(t16);
    $[23] = api;
    $[24] = currentTestimonials;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[28] !== t15) {
    t16 = <Carousel opts={t14} className="w-full" setApi={setApi}><CarouselContent className="-ml-2 md:-ml-4">{t15}</CarouselContent></Carousel>;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t13 || $[31] !== t16) {
    t17 = <a href="https://share.google/TypTi4R5U0dhoCRBC" target="_blank" rel="noopener noreferrer" className="relative max-w-6xl mx-auto px-4"><div className="relative">{t13}{t16}</div></a>;
    $[30] = t13;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t17 || $[34] !== t9) {
    t18 = <>{t9}{t17}</>;
    $[33] = t17;
    $[34] = t9;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  return t18;
};